export function bundleTotal(price, quantity) {
  if (!Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(price * quantity * 100)) return null;
  const percent = quantity >= 3 ? 18 : quantity === 2 ? 10 : 0;
  const original = price * quantity;
  return {percent, original, total: (price - Math.floor(price * percent / 100)) * quantity};
}

export function optionTotal(variant, quantity, cart) {
  const held = (cart?.items || []).filter(item=>String(item.variant_id)===String(variant.id) && !item.selling_plan_allocation).reduce((sum,item)=>sum + item.quantity,0);
  const tier = bundleTotal(variant.price,held + quantity);
  const original = variant.price * quantity;
  return tier ? {percent:tier.percent, original, total:(variant.price - Math.floor(variant.price * tier.percent / 100)) * quantity, held} : null;
}

export function purchasable(variant, cart) {
  if (!variant?.available || !cart || !Array.isArray(cart.items)) return 0;
  const held = cart.items.filter(item => String(item.variant_id) === String(variant.id)).reduce((n,item)=>n + item.quantity,0);
  const stock = variant.tracked && variant.policy !== 'continue' ? Math.max(0, variant.stock - held) : Number.MAX_SAFE_INTEGER;
  const maximum = variant.max == null ? stock : Math.min(stock, Math.max(0,variant.max - held));
  return Number.isSafeInteger(maximum) ? maximum : 0;
}

export function allowedQuantity(quantity, variant, cart) {
  return Number.isSafeInteger(quantity) && quantity >= (variant?.min || 1) && quantity <= purchasable(variant,cart) && quantity % (variant?.increment || 1) === 0;
}

export function projectedShipping({variant, quantity, cart, threshold, currency, extraCents = 500}) {
  if (!cart || cart.currency !== currency || currency !== 'USD' || cart.country !== 'US' || !Number.isSafeInteger(threshold) || threshold <= 0 || !Array.isArray(cart.items)) return null;
  if (cart.items.some(item => item.selling_plan_allocation || !Number.isSafeInteger(item.original_price) || !Number.isSafeInteger(item.quantity))) return null;
  const codes = [...new Set([...(cart.discount_codes || []).map(code => code.code.toUpperCase()), ...(cart.cart_level_discount_applications || []).filter(a=>a.type === 'discount_code').map(a=>(a.title || '').toUpperCase())])];
  const knownTitles = /^(Bundle savings|QA Bundle savings|QA Bundles 10-18|QA EXTRA5|EXTRA5|EARLY10|FREESHIP|Complimentary Gift Fragrance|QA Complimentary Gift Fragrance)$/i;
  const applications = [...(cart.cart_level_discount_applications || []), ...cart.items.flatMap(item=>(item.line_level_discount_allocations || []).map(a=>a.discount_application))];
  if (codes.some(code=>!['EARLY10','FREESHIP'].includes(code)) || applications.some(a=>!knownTitles.test(a.title || ''))) return null;
  const priced = optionTotal(variant,quantity,cart);
  if (!priced) return null;
  const gross = priced.original;
  const bundle = gross - priced.total;
  const extra = codes.some(code=>code!=='FREESHIP') ? 0 : Math.min(extraCents,gross);
  const coupon = codes.includes('EARLY10') ? Math.floor(gross/10) : 0;
  const projected = gross - Math.max(bundle,extra,coupon);
  if(!Number.isSafeInteger(projected)||projected<0)return null;
  return {remaining:Math.max(0,threshold-projected),projected,bundle,alternative:Math.max(extra,coupon)};
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('oa-quantity-bundles')) {
  class OAQuantityBundles extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try { this.config = JSON.parse(this.querySelector('[data-oa-config]').textContent); } catch { return; }
      this.form = this.closest('form');
      if (!this.form || this.config.currency !== 'USD') return;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.cart = this.config.fixtureCart || null;
      this.money = new Intl.NumberFormat(this.config.locale || 'en-US',{style:'currency',currency:this.config.currency});
      this.form.addEventListener('input',event => {
        if (!event.target.matches('[name="quantity"]')) return;
        this.selected = Number(event.target.value); this.render();
      },options);
      this.form.addEventListener('change',event => {
        if (event.target.matches('[data-oa-option]')) {this.selected = Number(event.target.value); this.syncQuantity(); this.render();}
        else if (event.target.matches('[name="quantity"]')) {this.selected = Number(event.target.value); this.render();}
        else this.updateVariant();
      },options);
      document.addEventListener('product:variant-change',()=>this.updateVariant(),options);
      this.variantObserver=new MutationObserver(()=>this.updateVariant());
      const variantInput=this.form.querySelector('[name="id"]');
      if(variantInput)this.variantObserver.observe(variantInput,{attributes:true,attributeFilter:['value']});
      document.addEventListener('product:quantity-update',event=> {
        if (String(event.detail?.variant?.id) !== String(this.variant?.id)) return;
        this.selected = Number(event.detail.quantity); this.render();
      },options);
      for (const event of ['cart:updated','cart:item-added','quick-cart:open']) document.addEventListener(event,()=>this.refreshCart(),options);
      window.addEventListener('focus',()=>this.refreshCart(),options);
      this.addEventListener('focusin',()=> {
        if (Date.now() - (this.cartReadAt || 0) > 15000) this.refreshCart();
      },options);
      const guardPurchase = event => {
        if (event.type === 'click' && !event.target.closest?.('[data-add-to-cart]')) return;
        if (this.config.previewOnly || !allowedQuantity(this.selected,this.variant,this.cart)) {
          event.preventDefault(); event.stopImmediatePropagation(); this.render();
          if (this.config.previewOnly) this.querySelector('[data-oa-status]').textContent = this.config.text.preview;
        }
      };
      this.form.addEventListener('click',guardPurchase,{...options,capture:true});
      this.form.addEventListener('submit',guardPurchase,{...options,capture:true});
      this.updateVariant();
      this.refreshCart();
    }
    disconnectedCallback() { this.variantObserver?.disconnect(); this.controller?.abort(); this.request?.abort(); this.controller = null; }
    updateVariant() {
      const id = this.form.querySelector('[name="id"]')?.value;
      const next = this.config.variants.find(v=>String(v.id)===String(id));
      if (next?.id === this.variant?.id) return;
      this.variant = next; this.selected = null;
      this.chooseDefault(); this.render();
    }
    chooseDefault() {
      if (!this.variant || !this.cart) return;
      if (!allowedQuantity(this.selected,this.variant,this.cart)) this.selected = [3,2,1].find(q=>allowedQuantity(q,this.variant,this.cart)) || null;
      this.syncQuantity();
    }
    syncQuantity() {
      if (!this.selected || !this.variant || !allowedQuantity(this.selected,this.variant,this.cart)) return;
      this.form.querySelectorAll('[name="quantity"],[data-quantity-input]').forEach(input=>{input.value=String(this.selected);});
      this.form.dispatchEvent(new Event('change',{bubbles:true}));
    }
    async refreshCart() {
      if (this.config.fixtureCart) {this.cartReadAt=Date.now(); this.chooseDefault(); this.render(); return;}
      this.request?.abort(); this.request = new AbortController();
      const request = this.request;
      try {
        const response = await fetch(this.config.stateUrl || this.config.cartUrl,{credentials:'same-origin',cache:'no-store',signal:request.signal});
        if (!response.ok) throw new Error('Cart unavailable');
        let cart;
        if(this.config.stateUrl){
          const sections=await response.json();
          const html=new DOMParser().parseFromString(sections['oa-bundle-state']||'', 'text/html');
          const state=JSON.parse(html.querySelector('[data-oa-cart-state]')?.textContent || 'null');
          if(!state?.cart)throw new Error('Cart state unavailable');
          cart=state.cart;cart.country=state.country;
          cart.items=cart.items.map(item=>({...item,...state.metadata.find(meta=>String(meta.variant_id)===String(item.variant_id))}));
        }else cart = await response.json();
        if (!Array.isArray(cart.items) || cart.currency !== this.config.currency) throw new Error('Cart invalid');
        if (request !== this.request || !this.isConnected) return;
        this.cart=cart; this.cartReadAt=Date.now(); this.chooseDefault(); this.render();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.cart=null; this.render();
      }
    }
    render() {
      const blocked = this.config.previewOnly || !allowedQuantity(this.selected,this.variant,this.cart);
      this.form.querySelectorAll('[data-add-to-cart]').forEach(button => {
        button.disabled = blocked; button.setAttribute('aria-disabled',String(blocked));
      });
      this.hidden = !this.variant;
      if (!this.variant) return;
      const text = this.config.text;
      this.querySelectorAll('[data-oa-option]').forEach(input=> {
        const quantity = Number(input.value), card = input.closest('label');
        const priced = optionTotal(this.variant,quantity,this.cart);
        input.disabled = !allowedQuantity(quantity,this.variant,this.cart);
        input.checked = quantity === this.selected && !input.disabled;
        card.querySelector('[data-oa-total]').textContent = priced ? this.money.format(priced.total/100) : '—';
        const original = card.querySelector('[data-oa-original]');
        original.hidden = !priced?.percent;
        original.textContent = priced ? this.money.format(priced.original/100) : '';
        const sub = card.querySelector('.oa-qb__sub');
        if (!sub.dataset.original) sub.dataset.original = sub.textContent.trim();
        sub.textContent = priced?.held && priced.percent ? text.cartTier.replace('{percent}',String(priced.percent)) : sub.dataset.original;
        const shipping = projectedShipping({variant:this.variant,quantity,cart:this.cart,threshold:this.config.threshold,currency:this.config.currency,extraCents:this.config.extraCents});
        card.querySelector('[data-oa-shipping]').textContent = input.disabled ? text.unavailable : !shipping ? text.shippingPending : shipping.remaining > 0 ? text.shippingRemaining.replace('{amount}',this.money.format(shipping.remaining/100)) : text.shippingEligible;
      });
      this.querySelector('[data-oa-status]').textContent = !this.cart ? text.cartPending : !this.selected ? text.unavailable : this.selected > 3 ? text.more.replace('{quantity}',String(this.selected)) : '';
    }
  }
  customElements.define('oa-quantity-bundles',OAQuantityBundles);
}
